<script setup lang="ts">
import { Icons } from '@/models/icons.enum';
import { computed, ref, nextTick } from 'vue';
import BackstageImage from '@/components/common/BackstageImage.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import { AllowableProductModel } from '@/models/allowable-product-model.model';
import { ProductDetailsModelImage } from '@/models/product-details-image.model';
import { useI18n } from 'vue-i18n';
import { ButtonVariant } from '@/models/button-variant.enum';

const props = defineProps<{
  productModel: AllowableProductModel;
  hideSelectButton: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
  close: [];
}>();

const getProductModelImages = (): ProductDetailsModelImage[] => {
  if (!props.productModel || !props.productModel.defaultVariantImages) {
    return [];
  }

  return props.productModel.defaultVariantImages.map((image, index) => ({
    url: image,
    selected: index === 0,
  }));
};

const currentSelectedImage = ref(new ProductDetailsModelImage());

const productModelImages = ref(getProductModelImages());

const findSelectedImage = computed(() => {
  const selectedImage = productModelImages.value.find((image) => image.selected) ?? productModelImages.value[0];

  return selectedImage?.url;
});

const productModelSelected = ref(props.productModel?.selected);

const toggleProductModelSelection = async () => {
  productModelSelected.value = !productModelSelected.value;

  await nextTick(() => {
    emit('toggle');
  });
};

const close = () => {
  emit('close');
};

const selectImage = (selectedImage: ProductDetailsModelImage) => {
  currentSelectedImage.value = selectedImage;
  productModelImages.value.forEach((image: ProductDetailsModelImage) => {
    image.selected = image.url === selectedImage.url;
  });
};

const { t } = useI18n();

const getOrderActionText = computed(() => {
  return productModelSelected.value ? t('create_edit_order.remove_from_order') : t('create_edit_order.add_to_order');
});
</script>

<template>
  <div class="view-product-details">
    <div class="view-product-details-cnt">
      <div class="product-details-top-bar" data-cy="top-bar">
        <div data-cy="product-model-name">{{ productModel?.name }}</div>
        <div class="product-model-action">
          <BackstageButton
            v-if="!hideSelectButton"
            :icon-right="productModelSelected ? Icons.SUBTRACT_SIGN : Icons.ADDITION_SIGN"
            @click="toggleProductModelSelection"
            data-cy="toggle-from-order-btn-cnt"
            data-cy-button="toggle-from-order-btn">
            {{ getOrderActionText }}
          </BackstageButton>
          <BackstageButton
            :variant="ButtonVariant.SECONDARY"
            icon-right="x-close"
            @click="close"
            data-cy="close-button-cnt"
            data-cy-button="close-button" />
        </div>
      </div>
      <div class="product-details-images">
        <div class="current-image" data-cy="current-image">
          <BackstageImage :src="findSelectedImage" alt="Selected Image" />
        </div>
        <div class="image-carousel" data-cy="image-carousel">
          <BackstageImage
            v-for="image of productModelImages"
            :key="image.url"
            class="image-carousel-item"
            :class="[{ selected: image.selected }]"
            @click="selectImage(image)"
            :src="`${image.url}?w=200`"
            alt="Selected Image" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.view-product-details {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  overflow-y: scroll;
  background-color: var(--bg-page-color);

  .view-product-details-cnt {
    @include common-container;
    padding: 20px;

    .product-details-top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      box-shadow:
        0px 0px 3px var(--shadow-color),
        0px 12px 24px var(--shadow-color);
      border-radius: 8px;
      background-color: var(--primary-bg-color);

      p {
        margin: 0;
      }

      .product-model-action {
        display: flex;
        align-items: center;
        gap: 16px;

        button {
          margin-right: 20px;

          &:last-child {
            margin: 0;
          }

          &.btn-close {
            position: relative;
            width: 42px;

            .ico-close {
              height: 24px;
              width: 24px;
              display: block;
              left: calc(50% - 11.5px);
              position: absolute;
              top: calc(50% - 11.5px);
            }
          }
        }
      }
    }

    .product-details-images {
      height: calc(100vh - 114px);
      display: flex;
      flex-direction: column;

      .current-image {
        width: 100%;
        padding: 10px 0;
        height: 77%;

        img {
          max-width: 100%;
          height: 100%;
        }
      }

      .image-carousel {
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        justify-content: flex-end;
        padding-bottom: 2rem;

        @include breakpoint(medium) {
          justify-content: center;
        }

        .image-carousel-item {
          height: 13rem;
          cursor: pointer;
          aspect-ratio: 3 / 2;
          object-fit: contain;
          border-radius: 4px;
          box-shadow:
            0px 0px 4px var(--shadow-color),
            0px 16px 32px var(--shadow-color);

          &.selected {
            outline: 2px solid var(--new-primary-color);
          }
        }
      }
    }
  }
}
</style>
